import type { ChatMessage, ChatPage, Clan, ClanList, ClanResult, Faith } from '@mezha/api'
import { baseApi } from '../baseApi'

export type Channel = 'place' | 'private' | 'clan' | 'system'

export const socialApi = baseApi.injectEndpoints({
  endpoints: b => ({
    chat: b.query<ChatPage, Channel>({
      query: channel => ({ get: `/api/v1/chat?channel=${channel}` }),
      providesTags: (_r, _e, channel) => [{ type: 'Chat', id: channel }],
    }),
    olderChat: b.query<ChatPage, { channel: Channel; before: number }>({
      query: ({ channel, before }) => ({ get: `/api/v1/chat?channel=${channel}&before=${before}` }),
    }),
    send: b.mutation<ChatMessage, { channel: Exclude<Channel, 'system'>; text: string; to?: number }>({
      query: body => ({ command: '/api/v1/chat', body }),
      // The sender sees the message from the answer at once; the event with the same id is skipped.
      onQueryStarted: async ({ channel }, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(socialApi.util.updateQueryData('chat', channel, page => {
          if (!page.messages.some(m => m.id === data.id)) page.messages.unshift(data)
        }))
      },
    }),
    report: b.mutation<{ report_id: number }, { message_id: number; reason_code: string; comment?: string }>({
      query: body => ({ command: '/api/v1/chat/report', body }),
    }),
    clans: b.query<ClanList, void>({ query: () => ({ get: '/api/v1/clans' }), providesTags: ['Clans'] }),
    clan: b.query<Clan, number>({ query: id => ({ get: `/api/v1/clans/${id}` }), providesTags: (_r, _e, id) => [{ type: 'Clan', id }] }),
    clanCommand: b.mutation<ClanResult, { path: string; body?: Record<string, unknown> }>({
      query: ({ path, body }) => ({ command: `/api/v1/clans${path}`, body }),
      invalidatesTags: ['Clans', 'Clan', 'Hero', { type: 'Chat', id: 'clan' }],
    }),
    faith: b.query<Faith, void>({ query: () => ({ get: '/api/v1/faith' }), providesTags: ['Faith'] }),
    chooseGod: b.mutation<Faith, { god_id: string }>({
      query: body => ({ command: '/api/v1/faith/choose', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(socialApi.util.upsertQueryData('faith', undefined, data))
        dispatch(baseApi.util.invalidateTags(['Hero', 'Loadout']))
      },
    }),
  }),
})

export const {
  useChatQuery, useLazyOlderChatQuery, useSendMutation, useReportMutation,
  useClansQuery, useClanQuery, useClanCommandMutation, useFaithQuery, useChooseGodMutation,
} = socialApi
